import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Globe, Download, Eye, Sparkles, Zap, Crown, BookOpen, Video, FileText, Rocket } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { websiteTemplates, industries } from '../websiteGenerator';
import axios from 'axios';
import { PRICING, formatPrice } from '../constants/pricing';
import DoneForYouCallout from '../components/DoneForYouCallout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const WebsiteGenerator = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    businessName: '',
    businessDescription: '',
    industry: '',
    contactEmail: '',
    contactPhone: '',
    address: '',
    websiteUrl: '',
    template: '',
    imageSource: 'industry',
    customBg: '#0d1117',
    customPrimary: '#3b82f6',
    customSecondary: '#6366f1',
    customText: '#f1f5f9',
  });
  const [websiteData, setWebsiteData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [liveUrl, setLiveUrl] = useState(null);
  const { toast } = useToast();
  const { user, profile, isPaid } = useAuth();

  // Load data from chat if available
  React.useEffect(() => {
    const savedData = localStorage.getItem('websiteData');
    if (savedData) {
      try {
        const data = JSON.parse(savedData);
        setFormData(prev => ({
          ...prev,
          businessName: data.topic || '',
          businessDescription: data.content || data.topic || ''
        }));
        localStorage.removeItem('websiteData');
        toast({
          title: 'Data Loaded!',
          description: 'AI suggestions have been pre-filled for you',
        });
      } catch (e) {
        console.error('Error loading website data:', e);
      }
    }
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGenerate = async () => {
    if (!formData.businessName || !formData.businessDescription || !formData.industry || !formData.template) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in all required fields',
        variant: 'destructive'
      });
      return;
    }

    if (!user) {
      toast({
        title: 'Authentication Required',
        description: 'Please sign in to generate a website.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      const payload = {
        user_id: user.id,
        business_name: formData.businessName,
        industry: formData.industry,
        description: formData.businessDescription,
        contact_email: formData.contactEmail || "info@example.com",
        contact_phone: formData.contactPhone || "(555) 123-4567",
        about_us: formData.aboutUs || "",
        address: formData.address || "",
        website_url: formData.websiteUrl || "",
        style: formData.template || "modern",
        image_source: formData.imageSource || "industry",
        is_paid_user: isPaid() || profile?.is_admin === true,
        user_email: profile?.email || user.email || "",
        custom_bg: formData.customBg,
        custom_primary: formData.customPrimary,
        custom_secondary: formData.customSecondary,
        custom_text: formData.customText,
      };

      console.log('🚀 Sending:', payload);

      // Start the job — returns immediately with job_id
      const startResp = await axios.post(`${BACKEND_URL}/api/ai/website/generate`, payload);
      const jobId = startResp.data.job_id;
      console.log('⏳ Job started:', jobId);

      // Poll for completion every 6 seconds (generation takes ~5 min)
      let result = null;
      for (let attempt = 0; attempt < 150; attempt++) {
        await new Promise(r => setTimeout(r, 6000));
        const statusResp = await axios.get(`${BACKEND_URL}/api/ai/website/status/${jobId}`);
        const data = statusResp.data;
        console.log(`🔄 Poll ${attempt + 1}:`, data.status);

        if (data.status === 'done') {
          result = data;
          break;
        }
        if (data.status === 'error') {
          throw new Error(data.detail || 'Generation failed on server');
        }
      }

      if (!result) {
        throw new Error('Generation timed out. The AI is still working — please try again in a few minutes.');
      }

      setWebsiteData({
        projectId: result.project_id,
        downloadUrl: result.download_url,
        previewUrl: result.preview_url,
        tier: result.tier,
        message: result.message,
        files: result.files || []
      });

      setStep(3);

      toast({
        title: '✨ Website Generated!',
        description: result.message || 'Your website is ready!',
      });
    } catch (error) {
      console.error('❌ Error:', error);
      console.error('📊 Details:', error.response?.data);

      toast({
        title: 'Generation Failed',
        description: error.message || error.response?.data?.detail || 'Failed to generate website. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (websiteData?.downloadUrl) {
      // Create download link
      const downloadLink = `${BACKEND_URL}${websiteData.downloadUrl}`;
      window.location.href = downloadLink;
      
      toast({
        title: 'Download Started',
        description: 'Your website ZIP file is downloading',
      });
    }
  };

  const handlePreview = () => {
    if (websiteData?.previewUrl) {
      const previewLink = `${BACKEND_URL}${websiteData.previewUrl}`;
      window.open(previewLink, '_blank');
    }
  };

  const handleDeploy = async () => {
    if (!websiteData?.projectId || !user?.id) return;
    setDeploying(true);
    try {
      const startResp = await axios.post(
        `${BACKEND_URL}/api/ai/website/deploy/${user.id}/${websiteData.projectId}`,
        {
          customer_email: profile?.email || user.email,
          customer_name: profile?.full_name || profile?.email?.split('@')[0] || 'Customer',
        }
      );
      const deployJobId = startResp.data.job_id;

      // Poll for deploy completion every 3 seconds
      for (let i = 0; i < 40; i++) {
        await new Promise(r => setTimeout(r, 3000));
        const statusResp = await axios.get(`${BACKEND_URL}/api/ai/website/deploy/status/${deployJobId}`);
        const data = statusResp.data;
        if (data.status === 'done') {
          setLiveUrl(data.live_url);
          toast({ title: '🚀 Site is Live!', description: `Deployed to ${data.live_url}` });
          return;
        }
        if (data.status === 'error') {
          throw new Error(data.detail || 'Deploy failed on server');
        }
      }
      throw new Error('Deploy timed out');
    } catch (err) {
      toast({
        title: 'Deploy Failed',
        description: err.message || err.response?.data?.detail || 'Could not deploy. Try again.',
        variant: 'destructive',
      });
    } finally {
      setDeploying(false);
    }
  };

  return (
    <div className="min-h-screen py-8 px-6">
      <div className="max-w-6xl mx-auto">
        <DoneForYouCallout />
        <div className="text-center mb-12">
          <div className="inline-block p-4 bg-blue-500/10 rounded-full mb-4">
            <Globe className="w-12 h-12 text-blue-400" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">Self-service Website Builder</h1>
          <p className="text-gray-400 text-lg mb-4">Build and manage a website yourself using the self-service tools.</p>
          <Badge className="bg-green-500/20 text-green-400 text-sm">
            <Sparkles className="w-3 h-3 mr-1" />
            Free preview — self-service PRO sold separately
          </Badge>
        </div>

        <div className="flex justify-center mb-12">
          <div className="flex items-center gap-4">
            {[
              { num: 1, label: 'Business Info' },
              { num: 2, label: 'Design' },
              { num: 3, label: 'Generate' }
            ].map((s, idx) => (
              <React.Fragment key={s.num}>
                <div className={`flex items-center gap-2 ${step >= s.num ? 'opacity-100' : 'opacity-40'}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                    step >= s.num ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-400'
                  }`}>
                    {s.num}
                  </div>
                  <span className="text-gray-300 text-sm">{s.label}</span>
                </div>
                {idx < 2 && (
                  <div className={`w-12 h-1 rounded ${step > s.num ? 'bg-blue-600' : 'bg-gray-700'}`}></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {step === 1 && (
          <Card className="bg-gray-800/50 border-gray-700 p-8">
            <h2 className="text-2xl font-bold text-white mb-6">Tell Us About Your Business</h2>
            
            <div className="space-y-6">
              <div>
                <Label className="text-gray-300 mb-2 block">Business Name *</Label>
                <Input
                  placeholder="e.g., Acme Corp"
                  value={formData.businessName}
                  onChange={(e) => handleInputChange('businessName', e.target.value)}
                  className="bg-gray-900 border-gray-700 text-white"
                />
              </div>

              <div>
                <Label className="text-gray-300 mb-2 block">Business Description *</Label>
                <Textarea
                  placeholder="Describe what your business does, your services, or your mission..."
                  value={formData.businessDescription}
                  onChange={(e) => handleInputChange('businessDescription', e.target.value)}
                  className="bg-gray-900 border-gray-700 text-white min-h-[120px]"
                />
              </div>

              <div>
                <Label className="text-gray-300 mb-2 block">Industry *</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {industries.map((industry) => (
                    <div
                      key={industry.id}
                      onClick={() => handleInputChange('industry', industry.id)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.industry === industry.id
                          ? 'border-blue-500 bg-blue-500/10'
                          : 'border-gray-700 bg-gray-900 hover:border-gray-600'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">{industry.icon}</span>
                        <div>
                          <div className="text-white font-semibold">{industry.name}</div>
                          <div className="text-gray-400 text-sm">{industry.keywords}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300 mb-2 block">Contact Email</Label>
                  <Input
                    type="email"
                    placeholder="contact@yourbusiness.com"
                    value={formData.contactEmail}
                    onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                    className="bg-gray-900 border-gray-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300 mb-2 block">Contact Phone</Label>
                  <Input
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={formData.contactPhone}
                    onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                    className="bg-gray-900 border-gray-700 text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300 mb-2 block">Business Address <span className="text-gray-500 text-xs">(optional)</span></Label>
                  <Input
                    type="text"
                    placeholder="123 Main St, City, State 12345"
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    className="bg-gray-900 border-gray-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300 mb-2 block">Current Website <span className="text-gray-500 text-xs">(optional)</span></Label>
                  <Input
                    type="url"
                    placeholder="https://yourbusiness.com"
                    value={formData.websiteUrl}
                    onChange={(e) => handleInputChange('websiteUrl', e.target.value)}
                    className="bg-gray-900 border-gray-700 text-white"
                  />
                </div>
              </div>

              <div>
                <Label className="text-gray-300 mb-2 block">About Us <span className="text-gray-500 text-xs">(optional — replaces Lorem ipsum)</span></Label>
                <textarea
                  placeholder="Tell us about your business, your story, your team..."
                  value={formData.aboutUs || ''}
                  onChange={(e) => handleInputChange('aboutUs', e.target.value)}
                  rows={3}
                  className="w-full bg-gray-900 border border-gray-700 text-white rounded-md px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <Button
                onClick={() => {
                  setStep(2);
                  if (!formData.template) {
                    const first = websiteTemplates.find(t => isPaid() || t.free);
                    if (first) handleInputChange('template', first.id);
                  }
                }}
                disabled={!formData.businessName || !formData.businessDescription || !formData.industry}
                className="w-full bg-blue-600 hover:bg-blue-700"
                size="lg"
              >
                Continue to Design <Sparkles className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </Card>
        )}

        {step === 2 && (
          <Card className="bg-gray-800/50 border-gray-700 p-8">
            <h2 className="text-2xl font-bold text-white mb-6">Choose Your Design</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              {websiteTemplates
                .filter(template => isPaid() || template.free)
                .map((template) => (
                <Card
                  key={template.id}
                  onClick={() => handleInputChange('template', template.id)}
                  className={`cursor-pointer transition-all duration-300 ${
                    formData.template === template.id
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-gray-700 bg-gray-800/50 hover:bg-gray-800'
                  }`}
                >
                  <div className="relative">
                    <img
                      src={template.preview}
                      alt={template.name}
                      className="w-full h-40 object-cover rounded-t-lg"
                    />
                    {!template.free && (
                      <div className="absolute top-2 right-2">
                        <Badge className="bg-yellow-500/90 text-white">
                          <Crown className="w-3 h-3 mr-1" />
                          PRO
                        </Badge>
                      </div>
                    )}
                  </div>
                  {/* Color scheme bar */}
                  <div className="flex h-5 w-full overflow-hidden">
                    <span style={{flex:1, background: template.bg || '#0d1117'}} title="Background" />
                    <span style={{flex:1, background: template.primary}} title={`Primary: ${template.primary}`} />
                    <span style={{flex:1, background: template.secondary}} title={`Secondary: ${template.secondary}`} />
                    <span style={{flex:1, background: template.textCol || '#f1f5f9'}} title="Text" />
                  </div>
                  <div className="p-3">
                    <h3 className="text-white font-semibold text-sm mb-0.5">{template.name}</h3>
                    <p className="text-gray-400 text-xs">{template.description}</p>
                  </div>
                </Card>
              ))}
            </div>

            {/* Custom colors card */}
            <Card
              onClick={() => handleInputChange('template', 'custom')}
              className={`cursor-pointer transition-all duration-300 mb-2 ${
                formData.template === 'custom'
                  ? 'border-blue-500 bg-blue-500/10'
                  : 'border-gray-700 bg-gray-800/50 hover:bg-gray-800'
              }`}
            >
              <div className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-white font-semibold">🎨 Custom Colors</h3>
                  <div className="flex gap-1 ml-auto">
                    <span style={{background: formData.customPrimary, width:14, height:14, borderRadius:'50%', display:'inline-block', border:'1px solid rgba(255,255,255,0.2)'}} />
                    <span style={{background: formData.customSecondary, width:14, height:14, borderRadius:'50%', display:'inline-block', border:'1px solid rgba(255,255,255,0.2)'}} />
                  </div>
                </div>
                <p className="text-gray-400 text-xs">Pick your own background, accent, and text colors.</p>
              </div>
            </Card>

            {/* Color pickers — shown only when Custom is selected */}
            {formData.template === 'custom' && (
              <div className="bg-gray-900/70 border border-blue-500/30 rounded-xl p-4 mb-4 grid grid-cols-2 gap-4">
                {[
                  { field: 'customBg',        label: 'Background' },
                  { field: 'customPrimary',   label: 'Primary Accent' },
                  { field: 'customSecondary', label: 'Secondary Accent' },
                  { field: 'customText',      label: 'Text Color' },
                ].map(({ field, label }) => (
                  <label key={field} className="flex flex-col gap-1">
                    <span className="text-gray-400 text-xs">{label}</span>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={formData[field]}
                        onChange={e => handleInputChange(field, e.target.value)}
                        className="w-10 h-8 rounded cursor-pointer border-0 bg-transparent"
                      />
                      <span className="text-gray-300 text-xs font-mono">{formData[field]}</span>
                    </div>
                  </label>
                ))}
              </div>
            )}

            {!isPaid() && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <Crown className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-white font-semibold mb-1">Self-service PRO templates</p>
                    <p className="text-gray-300 text-sm mb-3">
                      Self-service PRO adds Startup, Restaurant, Portfolio, E-commerce, Medical, Agency & Luxury templates plus professional images.
                    </p>
                    <Link to="/upgrade">
                      <Button size="sm" className="bg-yellow-600 hover:bg-yellow-700">
                        Self-service PRO — {formatPrice(PRICING.PRO_PRICE)} lifetime
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* Image Source Toggle */}
            <div className="bg-gray-900/50 border border-gray-700 rounded-xl p-4 mb-2">
              <p className="text-gray-300 text-sm font-medium mb-3">📸 Photo style:</p>
              <div className="flex gap-3">
                {[
                  { value: 'industry', label: '✍️ My Business Content', desc: 'Photos matched to what you typed' },
                  { value: 'template', label: '🎨 Template Aesthetic', desc: 'Photos matched to the color theme' }
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => handleInputChange('imageSource', opt.value)}
                    className={`flex-1 rounded-xl p-3 text-left border transition-all ${
                      formData.imageSource === opt.value
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-gray-700 bg-gray-800/50 hover:bg-gray-800'
                    }`}
                  >
                    <p className="text-white text-sm font-semibold">{opt.label}</p>
                    <p className="text-gray-400 text-xs mt-0.5">{opt.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => setStep(1)}
                variant="outline"
                className="border-gray-700 text-gray-300 hover:bg-gray-800"
              >
                Back
              </Button>
              <Button
                onClick={handleGenerate}
                disabled={!formData.template || loading}
                className="flex-1 bg-green-600 hover:bg-green-700"
                size="lg"
              >
                {loading ? (
                  <>
                    <Sparkles className="w-5 h-5 mr-2 animate-spin" />
                    {isPaid() ? 'AI Researching + Building (~30 sec)...' : 'AI Building Your Site (~5 min)...'}
                  </>
                ) : (
                  <>
                    <Zap className="w-5 h-5 mr-2" />
                    Generate My Website
                  </>
                )}
              </Button>
            </div>

            {loading && (
              <div className="mt-4 bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 text-center">
                <p className="text-blue-300 font-semibold mb-1">
                  {isPaid() ? '🧠 Your AI team is building your site...' : '🤖 Your AI team is building your site...'}
                </p>
                <p className="text-gray-400 text-sm">
                  {isPaid()
                    ? '7 specialized agents researching, writing & refining — all working in parallel just for you. Almost there!'
                    : 'Free tier: Your AI ensemble is generating — takes ~5 minutes. Keep this tab open, do not refresh!'}
                </p>
                <div className="mt-3 flex justify-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: `${i * 0.15}s`}} />
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}

        {step === 3 && websiteData && (
          <div className="space-y-6">
            <Card className="bg-gray-800/50 border-gray-700 p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">Your Website is Ready! 🎉</h2>
                  <p className="text-gray-400">Preview your website or download the ZIP file</p>
                </div>
                <Badge className="bg-green-500/20 text-green-400">
                  <Sparkles className="w-3 h-3 mr-1" />
                  {websiteData.tier === 'paid' ? 'Premium' : 'Free'} Tier
                </Badge>
              </div>

              <div className="flex gap-3 mb-4 flex-wrap">
                <Button
                  onClick={handlePreview}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  size="lg"
                >
                  <Eye className="w-5 h-5 mr-2" />
                  Preview Website
                </Button>
                <Button
                  onClick={handleDownload}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  size="lg"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Download ZIP
                </Button>
                {(isPaid() || profile?.is_admin) && (
                  <Button
                    onClick={handleDeploy}
                    disabled={deploying}
                    className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
                    size="lg"
                  >
                    <Rocket className="w-5 h-5 mr-2" />
                    {deploying ? 'Deploying...' : 'Deploy Live'}
                  </Button>
                )}
              </div>

              {liveUrl && (
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4 mb-4">
                  <p className="text-purple-300 text-sm font-semibold mb-1">🚀 Your site is live!</p>
                  <a
                    href={liveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:underline break-all text-sm"
                  >
                    {liveUrl}
                  </a>
                  {(isPaid() || profile?.is_admin) && (
                    <div className="mt-3 pt-3 border-t border-purple-500/20">
                      <p className="text-gray-400 text-xs mb-2">Want changes? Regenerate and redeploy to the same URL.</p>
                      <Button
                        onClick={() => {
                          // Keep business info but clear template so user picks colors fresh
                          setFormData(prev => ({ ...prev, template: '' }));
                          setWebsiteData(null);
                          setLiveUrl(null);
                          setStep(2);
                        }}
                        size="sm"
                        className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs"
                      >
                        ✏️ Update My Site
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {websiteData.files && websiteData.files.length > 0 && (
                <div className="bg-gray-900/50 rounded-lg p-4 mb-6">
                  <h3 className="text-white font-semibold mb-2">Generated Files:</h3>
                  <ul className="text-gray-300 text-sm space-y-1">
                    {websiteData.files.map((file, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <span className="text-green-400">✓</span> {file}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-6">
                {!isPaid() ? (
                  <>
                    <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                      <Crown className="w-5 h-5 text-yellow-400" />
                      Self-service PRO includes:
                    </h3>
                    <ul className="space-y-2 text-gray-300 text-sm mb-4">
                      <li>✨ Remove "Powered by globalnow.biz" branding</li>
                      <li>🎨 10+ premium templates with advanced customization</li>
                      <li>📱 Multi-page websites (About, Services, Contact pages)</li>
                      <li>🚀 SEO optimization & meta tags</li>
                      <li>🌐 Custom domain setup assistance</li>
                      <li>🔄 Unlimited regenerations and edits</li>
                    </ul>
                    <Link to="/upgrade">
                      <Button className="w-full bg-yellow-600 hover:bg-yellow-700">
                        Self-service PRO — {formatPrice(PRICING.PRO_PRICE)} lifetime
                      </Button>
                    </Link>
                  </>
                ) : (
                  <>
                    <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-blue-400" />
                      Pro Tips & Resources
                    </h3>
                    <div className="space-y-3">
                      <Card className="bg-gray-800/50 border-gray-700 p-3 hover:bg-gray-800/70 transition-all cursor-pointer">
                        <div className="flex items-start gap-3">
                          <BookOpen className="w-5 h-5 text-blue-400 mt-1" />
                          <div>
                            <p className="text-white font-medium text-sm">Website Best Practices</p>
                            <p className="text-gray-400 text-xs">Learn how to optimize your website for conversions</p>
                          </div>
                        </div>
                      </Card>
                      <Card className="bg-gray-800/50 border-gray-700 p-3 hover:bg-gray-800/70 transition-all cursor-pointer">
                        <div className="flex items-start gap-3">
                          <Video className="w-5 h-5 text-green-400 mt-1" />
                          <div>
                            <p className="text-white font-medium text-sm">Video Tutorials</p>
                            <p className="text-gray-400 text-xs">Watch step-by-step guides for advanced features</p>
                          </div>
                        </div>
                      </Card>
                      <Card className="bg-gray-800/50 border-gray-700 p-3 hover:bg-gray-800/70 transition-all cursor-pointer">
                        <div className="flex items-start gap-3">
                          <FileText className="w-5 h-5 text-purple-400 mt-1" />
                          <div>
                            <p className="text-white font-medium text-sm">SEO Guide</p>
                            <p className="text-gray-400 text-xs">Boost your website's search engine ranking</p>
                          </div>
                        </div>
                      </Card>
                    </div>
                  </>
                )}
              </div>
            </Card>

            <div className="flex flex-col gap-2 w-full">
              <button
                onClick={() => {
                  setFormData(prev => ({ ...prev, template: '' }));
                  setWebsiteData(null);
                  setLiveUrl(null);
                  setStep(2);
                }}
                className="w-full rounded-xl border border-indigo-500/50 bg-indigo-500/10 hover:bg-indigo-500/20 p-3 text-left transition-all"
              >
                <p className="text-white font-semibold text-sm">🎨 Try a Different Style</p>
                <p className="text-gray-400 text-xs mt-0.5">Keep your business info — just pick a new color theme and regenerate</p>
              </button>
              <button
                onClick={() => {
                  setStep(1);
                  setFormData({
                    businessName: '',
                    businessDescription: '',
                    industry: '',
                    contactEmail: '',
                    contactPhone: '',
                    aboutUs: '',
                    address: '',
                    websiteUrl: '',
                    template: '',
                    imageSource: 'industry',
                    customBg: '#0d1117',
                    customPrimary: '#3b82f6',
                    customSecondary: '#6366f1',
                    customText: '#f1f5f9',
                  });
                  setWebsiteData(null);
                  setLiveUrl(null);
                }}
                className="w-full rounded-xl border border-gray-700 bg-gray-800/50 hover:bg-gray-800 p-3 text-left transition-all"
              >
                <p className="text-white font-semibold text-sm">🆕 Start a New Website</p>
                <p className="text-gray-400 text-xs mt-0.5">Clear everything and build a site for a different business</p>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WebsiteGenerator;
